import React from 'react';
import { ArrowLeft, Download, Code, BookOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useLanguage } from '../contexts/LanguageContext';

const renderText = (text) => {
  if (typeof text !== 'string') return text;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="text-foreground">{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

export const TechnicalPaper = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const content = t('technicalPaper.content');

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('navigation.backToHome')}
          </Button>
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_567a6262-0291-4069-9a05-5048d6f99ff3/artifacts/3eapd20t_logo.png"
              alt="Shenbury"
              className="h-8 w-8"
            />
            <span className="text-xl font-serif font-semibold text-gradient-primary">Shenbury</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Title Section */}
          <div className="text-center mb-12">
            <Badge variant="outline" className="glass border-primary/30 mb-4">
              <Code className="w-4 h-4 mr-2 inline" />
              {t('technicalPaper.draft')}
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-serif font-bold mb-4">
              {t('technicalPaper.title')}
            </h1>
            <p className="text-xl font-serif text-muted-foreground mb-4">
              {t('technicalPaper.subtitle')}
            </p>
            <div className="flex items-center justify-center gap-6 text-sm text-muted-foreground">
              <span>{t('technicalPaper.date')}</span>
              <span>•</span>
              <span>{t('technicalPaper.team')}</span>
            </div>
          </div>

          {/* Abstract */}
          <Card className="glass border-card-border mb-8">
            <CardHeader>
              <CardTitle className="text-2xl font-serif text-gradient-gold">{content.abstract.heading}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground leading-relaxed">
              {content.abstract.paragraphs.map((p, i) => (
                <p key={i}>{renderText(p)}</p>
              ))}
            </CardContent>
          </Card>

          {/* Introduction */}
          <Card className="glass border-card-border mb-8">
            <CardHeader>
              <CardTitle className="text-2xl font-serif text-foreground">{content.introduction.heading}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground leading-relaxed">
              {content.introduction.paragraphs.map((p, i) => (
                <p key={i}>{renderText(p)}</p>
              ))}
            </CardContent>
          </Card>

          {/* Agentic Manifesto */}
          <Card className="glass border-secondary/20 shadow-gold mb-8">
            <CardHeader>
              <CardTitle className="text-2xl font-serif text-gradient-gold">{content.agenticManifesto.heading}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground leading-relaxed">
                {renderText(content.agenticManifesto.intro)}
              </p>
              <div className="space-y-4">
                {content.agenticManifesto.principles.map((principle, idx) => (
                  <div key={idx} className="p-4 rounded-lg glass border border-primary/20">
                    <h4 className="font-semibold text-foreground mb-2">{principle.title}</h4>
                    <p className="text-sm text-muted-foreground">{principle.desc}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* System Architecture */}
          <Card className="glass border-card-border mb-8">
            <CardHeader>
              <CardTitle className="text-2xl font-serif text-foreground">{content.systemArchitecture.heading}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-muted-foreground leading-relaxed">
                {renderText(content.systemArchitecture.intro)}
              </p>

              {/* Orchestration Layer */}
              <div className="border-l-4 border-primary pl-4">
                <h3 className="text-xl font-serif font-semibold text-foreground mb-2">{content.systemArchitecture.subsections.orchestration.heading}</h3>
                <p className="text-muted-foreground">
                  {content.systemArchitecture.subsections.orchestration.paragraph}
                </p>
              </div>

              {/* Swarm Coordination */}
              <div className="border-l-4 border-primary pl-4">
                <h3 className="text-xl font-serif font-semibold text-foreground mb-2">{content.systemArchitecture.subsections.swarmCoordination.heading}</h3>
                <p className="text-muted-foreground">
                  {content.systemArchitecture.subsections.swarmCoordination.paragraph}
                </p>
              </div>

              {/* AgentDB */}
              <div className="border-l-4 border-secondary pl-4">
                <h3 className="text-xl font-serif font-semibold text-foreground mb-2">{content.systemArchitecture.subsections.agentDB.heading}</h3>
                <p className="text-muted-foreground mb-3">
                  {renderText(content.systemArchitecture.subsections.agentDB.intro)}
                </p>
                <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground ml-4">
                  {content.systemArchitecture.subsections.agentDB.items.map((item, i) => (
                    <li key={i}>{renderText(item)}</li>
                  ))}
                </ul>
              </div>

              {/* ReasoningBank */}
              <div className="border-l-4 border-secondary pl-4">
                <h3 className="text-xl font-serif font-semibold text-foreground mb-2">{content.systemArchitecture.subsections.reasoningBank.heading}</h3>
                <p className="text-muted-foreground mb-3">
                  {renderText(content.systemArchitecture.subsections.reasoningBank.intro)}
                </p>
                <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground ml-4">
                  {content.systemArchitecture.subsections.reasoningBank.items.map((item, i) => (
                    <li key={i}>{renderText(item)}</li>
                  ))}
                </ol>
              </div>

              {/* Local Embeddings */}
              <div className="border-l-4 border-primary pl-4">
                <h3 className="text-xl font-serif font-semibold text-foreground mb-2">{content.systemArchitecture.subsections.localEmbeddings.heading}</h3>
                <p className="text-muted-foreground mb-3">
                  {renderText(content.systemArchitecture.subsections.localEmbeddings.intro)}
                </p>
                <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground ml-4">
                  {content.systemArchitecture.subsections.localEmbeddings.items.map((item, i) => (
                    <li key={i}>{renderText(item)}</li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Security Architecture */}
          <Card className="glass border-card-border mb-8">
            <CardHeader>
              <CardTitle className="text-2xl font-serif text-foreground">{content.security.heading}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-muted-foreground leading-relaxed">
                {content.security.intro}
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="glass p-4 rounded-lg border border-destructive/20">
                  <h3 className="text-lg font-serif font-semibold text-foreground mb-3">{content.security.adversarialTitle}</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    {content.security.adversarialItems.map((item, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <div className="w-2 h-2 rounded-full bg-destructive mt-1.5 flex-shrink-0"></div>
                        <span><strong>{item.term}:</strong> {item.desc}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="glass p-4 rounded-lg border border-green-500/20">
                  <h3 className="text-lg font-serif font-semibold text-foreground mb-3">{content.security.cryptoTitle}</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    {content.security.cryptoItems.map((item, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <div className="w-2 h-2 rounded-full bg-green-500 mt-1.5 flex-shrink-0"></div>
                        <span><strong>{item.term}:</strong> {item.desc}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              <div className="border-l-4 border-primary pl-4 mt-6">
                <h3 className="text-xl font-serif font-semibold text-foreground mb-2">{content.security.faultTolerance.heading}</h3>
                <p className="text-muted-foreground">
                  {content.security.faultTolerance.paragraph}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Scalability & Performance */}
          <Card className="glass border-card-border mb-8">
            <CardHeader>
              <CardTitle className="text-2xl font-serif text-foreground">{content.scalability.heading}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-muted-foreground leading-relaxed">
                {content.scalability.intro}
              </p>
              
              <div className="grid gap-6">
                <div className="border-l-4 border-secondary pl-4">
                  <h3 className="text-xl font-serif font-semibold text-foreground mb-2">{content.scalability.horizontalScaling.heading}</h3>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                    {content.scalability.horizontalScaling.items.map((item, i) => (
                      <li key={i}>{renderText(item)}</li>
                    ))}
                  </ul>
                </div>
                
                <div className="border-l-4 border-secondary pl-4">
                  <h3 className="text-xl font-serif font-semibold text-foreground mb-2">{content.scalability.benchmarks.heading}</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse mt-3">
                      <thead>
                        <tr className="border-b border-border">
                          {content.scalability.benchmarks.headers.map((header, i) => (
                            <th key={i} className="text-left p-2 font-semibold text-foreground">{header}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {content.scalability.benchmarks.rows.map((row, i) => (
                          <tr key={i}>
                            <td className="p-2">{row.metric}</td>
                            <td className="p-2">{row.target}</td>
                            <td className="p-2 font-semibold text-green-500">{row.achieved}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                
                <div className="border-l-4 border-secondary pl-4">
                  <h3 className="text-xl font-serif font-semibold text-foreground mb-2">{content.scalability.resourceOptimization.heading}</h3>
                  <p className="text-muted-foreground mb-3">
                    {content.scalability.resourceOptimization.intro}
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                    {content.scalability.resourceOptimization.items.map((item, i) => (
                      <li key={i}>{renderText(item)}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Implementation & Deployment */}
          <Card className="glass border-card-border mb-8">
            <CardHeader>
              <CardTitle className="text-2xl font-serif text-foreground">{content.implementation.heading}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-muted-foreground leading-relaxed">
                {content.implementation.intro}
              </p>
              
              <div className="space-y-4">
                {content.implementation.phases.map((phase) => (
                  <div key={phase.num} className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-primary flex items-center justify-center font-serif font-bold text-sm">
                      {phase.num}
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground mb-1">{phase.title}</h4>
                      <p className="text-sm text-muted-foreground">{phase.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="border-l-4 border-primary pl-4 mt-6">
                <h3 className="text-xl font-serif font-semibold text-foreground mb-2">{content.implementation.techStack.heading}</h3>
                <div className="grid md:grid-cols-3 gap-4 mt-3">
                  {content.implementation.techStack.stacks.map((stack, i) => (
                    <div key={i} className="glass p-3 rounded-lg">
                      <h4 className="font-semibold text-foreground mb-2">{stack.heading}</h4>
                      <ul className="text-xs text-muted-foreground space-y-1">
                        {stack.items.map((item, j) => (
                          <li key={j}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Verification Workflow */}
          <Card className="glass border-card-border mb-8">
            <CardHeader>
              <CardTitle className="text-2xl font-serif text-foreground">{content.verificationWorkflow.heading}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {content.verificationWorkflow.steps.map((item) => (
                  <div key={item.step} className="flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-primary flex items-center justify-center font-serif font-bold">
                      {item.step}
                    </div>
                    <div className="flex-grow">
                      <h4 className="font-semibold text-foreground mb-1">{item.title}</h4>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Conclusion */}
          <Card className="glass border-secondary/20 shadow-gold mb-8">
            <CardHeader>
              <CardTitle className="text-2xl font-serif text-gradient-gold">{content.conclusion.heading}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground leading-relaxed">
              {content.conclusion.paragraphs.map((p, i) => (
                <p key={i}>{renderText(p)}</p>
              ))}
            </CardContent>
          </Card>

          {/* Footer */}
          <div className="text-center py-8 border-t border-border">
            <p className="text-sm text-muted-foreground mb-4">
              {content.footerCopyright}
            </p>
            <div className="flex justify-center gap-4">
              <Button variant="outline" className="gap-2">
                <Download className="w-4 h-4" />
                {t('common.downloadPDF')}
              </Button>
              <Button variant="outline" className="gap-2" onClick={() => navigate('/whitepaper')}>
                <BookOpen className="w-4 h-4" />
                {t('common.whitepaper')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
